import React, { forwardRef, useMemo, useReducer, useRef, useState } from "react";
import { MenuItem, MenuList } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
	environmentAllowed,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../../common/providers";
import styles from "./DocumentTableRowMenu.module.css";
import { CustomIconButton, DropDown, icon, IconComponent } from "../../../../common/components";
import { DOCUMENT_STATUS } from "../../constants/constants";
import { compareInsensitiveCaseString, endsWithOneOf } from "../../../../common/utils";
import { FILE_TYPES, FILE_TYPES_EXTENSIONS } from "../../../../common/constants/file-types";
import { useApi, useTrackEvent } from "../../../../common/hooks";
import {
	getContent,
	getTree,
	setHasReceivedModifications,
	setOpenUploadDialog,
	setRefreshSelectedDocument,
} from "../../slice/document-center-slice";
import { ApiService } from "../../../../api";
import { setQuestionsSelectedTab } from "../../../../common/slice";
import ConfirmDeleteDialog from "../confirm-delete-dialog/ConfirmDeleteDialog";
import DeleteLastVersionDialog from "../delete-last-version-dialog/DeleteLastVersionDialog";
import { onBeforeUnloadHandler } from "../../utils/utils";
import projectDocumentService from "../../../../api/services/project/project-document-service";
import projectDirectoryService from "../../../../api/services/project/project-directory-service";

function DocumentTableRowMenu({
	document,
	documentId: id,
	documentType: type,
	status,
	onOpenSidPanel,
	onOpenMoveDialog,
	onOpenVersionDialog,
	onOpenDownloadDialog,
}) {
	const dispatch = useDispatch();
	const fileInputRef = useRef();
	const menuButtonRef = useRef();
	const trackEvent = useTrackEvent();
	const [isOpenRowMenu, setIsOpenRowMenu] = useState(false);
	const [openVersionMenu, setOpenVersionMenu] = useState(false);
	const [deleteContent, setDeleteContent] = useState(true);
	const projectId = useSelector(({ context }) => context.project.id);
	const hasActionPermission = useSelector(({ documentCenter }) => documentCenter.hasActionPermission);
	const currentDirectoryId = useSelector(({ documentCenter }) => documentCenter.currentDirectoryId);
	const [openConfirmDeleteDialog, dispatchOpenConfirmDeleteDialog] = useReducer((prev) => !prev, false);
	const [openDeleteLastVersionDialog, dispatchOpenDeleteLastVersionDialog] = useReducer((prev) => !prev, false);
	const { call: onDelete } = useApi(projectDirectoryService.deleteSelection);
	const { call: onUploadNewVersion } = useApi(projectDocumentService.uploadNewVersion);
	const isUnsupported = useMemo(() => compareInsensitiveCaseString(type, FILE_TYPES.UNSUPPORTED), [type]);
	const isOngoing = useMemo(
		() => status === DOCUMENT_STATUS.IN_PROGRESS || status === DOCUMENT_STATUS.PENDING,
		[status]
	);
	const isFile = type !== "folder";
	const handleToggleRowMenu = () => {
		setIsOpenRowMenu((prev) => !prev);
	};
	const handleCloseRowMenu = () => {
		setIsOpenRowMenu(false);
	};
	const handleOpenSidPanel = (e) => {
		handleToggleRowMenu();
		onOpenSidPanel(e, document);
	};
	const handleOpenVersionMenu = (e) => {
		e.stopPropagation();
		setOpenVersionMenu((prev) => !prev);
	};
	const handleImportVersionFromSys = (e) => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}
		e.target.value = "";
		window.addEventListener("beforeunload", onBeforeUnloadHandler);

		onUploadNewVersion({ prevDocumentId: id }, file, null)
			.then(() => {
				dispatch(setHasReceivedModifications(true));
				trackEvent("Document_Manual_V2_Upload");
			})
			.finally(() => {
				window.removeEventListener("beforeunload", onBeforeUnloadHandler);
				dispatch(setOpenUploadDialog(true));
			});
	};
	const handleOpenVersionDialog = () => {
		onOpenVersionDialog(document);
		handleToggleRowMenu();
	};
	const handleTriggerFileInput = (e) => {
		handleToggleRowMenu();
		e.stopPropagation();
		fileInputRef.current?.click();
	};
	const handleCloseMoveDialog = () => {
		setQuestionsSelectedTab(0);
		dispatch(setRefreshSelectedDocument(true));
	};
	const resetTreeAndContent = () => {
		handleCloseMoveDialog();
		const { token } = ApiService.getCancelTokenSource();
		dispatch(getTree({ projectId, token }));
		dispatch(getContent({ projectId, directoryId: currentDirectoryId, token }));
		setDeleteContent(true);
	};
	const handleOpenMoveDialog = () => {
		handleToggleRowMenu();
		onOpenMoveDialog(document);
	};
	const handleDownload = () => {
		handleToggleRowMenu();
		onOpenDownloadDialog(document);
	};
	const handleSetDeleteContent = () => {
		setDeleteContent((prev) => !prev);
	};
	const handleDelete = () => {
		if (type === "folder") {
			dispatchOpenConfirmDeleteDialog();
		}
		onDelete(
			{ projectId },
			{
				fromDirectoryId: currentDirectoryId,
				deleteContent,
				directoryIds: type === "folder" ? [id] : [],
				documentIds: type === "folder" ? [] : [id],
			}
		)
			.then(resetTreeAndContent)
			.catch(console.error);
	};
	const handleOpenDeleteConfirmation = () => {
		handleToggleRowMenu();
		if (type === "folder") {
			dispatchOpenConfirmDeleteDialog();
		} else if (document?.version > 1) {
			dispatchOpenDeleteLastVersionDialog();
		} else if (endsWithOneOf(`.${type}`, FILE_TYPES_EXTENSIONS)) {
			dispatchOpenConfirmDeleteDialog();
		} else {
			handleDelete();
		}
	};
	const handleCloseLastVersionDialog = () => dispatchOpenDeleteLastVersionDialog();
	return (
		<>
			<CustomIconButton
				ref={menuButtonRef}
				btnClassName={styles.menuButton}
				icon={icon.faEllipsisV}
				size="sm"
				variant="text"
				onClick={handleToggleRowMenu}
			/>
			<input
				ref={fileInputRef}
				hidden
				accept=".doc,.docx,.odt,.pdf,.rtf,.xls,.xlsm,.xlsx,.zip"
				disabled={isOngoing}
				id="import"
				name="import"
				type="file"
				onChange={handleImportVersionFromSys}
			/>
			<DropDown ref={menuButtonRef} open={isOpenRowMenu} position="bottom-end" onClose={handleCloseRowMenu}>
				<MenuList className={styles.menuList}>
					<MenuItem className={styles.menuItem} onClick={handleOpenSidPanel}>
						<div className={styles.menuItem__icon}>
							<IconComponent icon={icon.faList} />
						</div>
						<span className={styles.menuItem__text}>
							{translate("document-center.table.row.menu-item.display-details")}
						</span>
					</MenuItem>
					{isFile &&
						hasActionPermission &&
						!isUnsupported &&
						!isOngoing &&
						isSegFeatureEnabled(SegFlags.VERSIONS) &&
						hasPermission(Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER) && (
							<MenuItem className={styles.menuItem} onClick={handleOpenVersionMenu}>
								<div className={styles.menuItem__icon}>
									<IconComponent icon={icon.faPlus} size="lg" />
								</div>
								<span className={styles.menuItem__text}>
									{translate("document-center.table.row.menu-item.add-version")}
								</span>
								<div className={styles.menuItem__icon}>
									<IconComponent icon={icon.faChevronRight} size="sm" />
								</div>
								{openVersionMenu && (
									<MenuList className={styles.subMenuList}>
										<MenuItem className={styles.menuItem} onClick={handleTriggerFileInput}>
											<span className={styles.menuItem__text}>
												<span className={styles.menuItem__text}>
													{translate("document-center.side-panel.versions.add-from-system")}
												</span>
											</span>
										</MenuItem>
										<MenuItem className={styles.menuItem} onClick={handleOpenVersionDialog}>
											<span className={styles.menuItem__text}>
												{translate("document-center.side-panel.versions.add-from-platform")}
											</span>
										</MenuItem>
									</MenuList>
								)}
							</MenuItem>
						)}
					{hasActionPermission && !isOngoing && (
						<MenuItem className={styles.menuItem} onClick={handleOpenMoveDialog}>
							<div className={styles.menuItem__icon}>
								<IconComponent icon={icon.faArrowRight} />
							</div>
							<span className={styles.menuItem__text}>
								{translate("document-center.table.row.menu-item.move")}
							</span>
						</MenuItem>
					)}
					{(environmentAllowed(["veolia"]) || hasActionPermission) && (
						<MenuItem className={styles.menuItem} onClick={handleDownload}>
							<div className={styles.menuItem__icon}>
								<IconComponent icon={icon.faDownload} />
							</div>
							<span className={styles.menuItem__text}>
								{translate("document-center.table.row.menu-item.download")}
							</span>
						</MenuItem>
					)}
					{hasActionPermission && (
						<MenuItem className={styles.menuItem} onClick={handleOpenDeleteConfirmation}>
							<div className={styles.menuItem__icon}>
								<IconComponent icon={icon.faTrash} />
							</div>
							<span className={styles.menuItem__text}>
								{document?.version > 1
									? translate("document-center.table.row.menu-item.delete-last-version")
									: translate("document-center.table.row.menu-item.delete")}
							</span>
						</MenuItem>
					)}
				</MenuList>
			</DropDown>
			<ConfirmDeleteDialog
				deleteContent={deleteContent}
				isFolder={!isFile}
				open={openConfirmDeleteDialog}
				text={translate(
					isFile
						? "document-center.table.confirm-delete.text.file"
						: "document-center.table.confirm-delete.text.folder"
				)}
				textSubDir={translate("document-center.table.confirm-delete.sub-text.folder")}
				onClose={dispatchOpenConfirmDeleteDialog}
				onConfirm={handleDelete}
				onSetDeleteContent={handleSetDeleteContent}
			/>
			<DeleteLastVersionDialog
				open={openDeleteLastVersionDialog}
				onClose={handleCloseLastVersionDialog}
				onConfirm={handleDelete}
			/>
		</>
	);
}

export default forwardRef(DocumentTableRowMenu);
